import React, {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router";
import {useTranslation} from "react-i18next";

import AlertIcon from "../../components/Icons/AlertIcon";
import { BadgeCheck as BadgeCheckIcon, MessageDots as ChatBubbleIcon } from "flowbite-react-icons/outline";
import CalendarXIcon from "../../components/Icons/CalendarXIcon";
import InformationCircleIcon from "../../components/Icons/InformationCircleIcon";
import ChevronRightIcon from "../../components/Icons/ChevronRightIcon";

import {Modal, ModalBody, ModalHeader} from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import RadioButton from "../../components/RadioButton";
import Api from "../../services/Api";
import {getLanguage, languageMap} from "../../lang/helpers";
import {StrArr, StrMap} from "../../interfaces/common";
import {formatDate} from "../../helpers/dateFunctions";
import {FeedbackType} from "../SurveyResult/interfaces";

interface AnswerInterface {
  answer: number;
  freeAnswer: string;
  showFreeAnswer: boolean;
}

interface QuestionInterface {
  questionId: string;
  questionNr: number;
  names: StrMap;
  noAnswer: boolean;
  freeText: boolean;
  answer: number;
  freeAnswer: string;
  answers: Array<AnswerInterface>
}

interface SectionInterface {
  names: StrMap;
  questions: Array<QuestionInterface>;
}

interface SurveyInterface {
  language: string;
  languages: string[];
  deadline: string;
  answerOptions: number;
  answerType: number;
  showNoAnswer: boolean;
  translations_answers: StrArr;
  names: StrMap;
  sections: Array<SectionInterface>
}

interface OtherSurveyInterface {
  id: string,
  name: string,
  deadline: string,
  token: string,
}

interface DataInterface {
  status: string;
  client_name: string;
  deadline: string;
  nr_feedback_users: number;
  feedbackType: FeedbackType
}

interface FeedbackRecipient {
  clientId: string;
  name: string;
}

interface AnswerOption {
  name: string;
  value: string;
}

const SurveyMultiResponse = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DataInterface | null>(null);
  const [otherSurveys, setOtherSurveys] = useState<Array<OtherSurveyInterface>>([]);
  const [survey, setSurvey] = useState<SurveyInterface>({
    language: "en",
    languages: [],
    deadline: "",
    answerOptions: 5,
    answerType: 0,
    showNoAnswer: true,
    names: {},
    translations_answers: {},
    sections: []
  });
  const [feedbackRecipients, setFeedbackRecipients] = useState<Array<FeedbackRecipient>>([]);
  const [answerOptions, setAnswerOptions] = useState<Array<AnswerOption>>([]);
  const [showFeedbackSent, setShowFeedbackSent] = useState(false);
  const [language, setLanguage] = useState("en");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const { survey_multi_recipient_id } = useParams();
  // State to store user responses
  const [showAlert, setShowAlert] = useState(false);
  const [selfInfo, setSelfInfo] = useState(false);
  const [optionColWidth, setOptionColWidth] = useState(120);
  const [sectionNr, setSectionNr] = useState(0);
  const [narrow, setNarrow] = useState(false);
  const [answerCols, setAnswerCols] = useState(5);

  const nameColWidth = 140;
  let url = "client/survey_multi/" + survey_multi_recipient_id;

  const setDefaultLanguage = (_survey: SurveyInterface) => {
    if (_survey.languages.indexOf(lang) !== -1) {
      // If browser language is in survey.languages, use that language
      setLanguage(lang);
    } else {
      // Else use survey language
      setLanguage(_survey.language);
    }
  }

  const formatAnswerOptions = (_survey: SurveyInterface, _language: string) => {
    let _answerOptions:Array<AnswerOption> = [];
    for (let i = 1; i <= _survey.answerOptions; i++) {
      _answerOptions.push({
        name: _survey.translations_answers[_language][i],
        value: i + ""
      })
    }
    // Do not add noAnswer for self-assessments
    if (_survey.showNoAnswer) {
      _answerOptions.push({
        name: _survey.translations_answers[_language][0],
        value: "0"
      })
    }
    setAnswerOptions(_answerOptions);
  }

  useEffect(() => {
    const anonymous = !!token;
    api
      .get(url, [], anonymous)
      .then((response) => {
        if (response.json.status === "ok") {
          setData(response.json.data);
          setSurvey(response.json.survey);
          setDefaultLanguage(response.json.survey);
          setFeedbackRecipients(response.json.feedbackRecipients);
          formatAnswerOptions(response.json.survey, language);
        } else {
          setError(response.json.error);
          setData(response.json);
        }
        setOtherSurveys(response.json.otherSurveys);
        setLoading(false);
      })
      .catch((err) => {
        alert("An error occurred: " + err);
      });

    window.addEventListener('resize', calculateOptionColWidth);

    return () => {
      window.removeEventListener('resize', calculateOptionColWidth);
    }
  }, []);

  useEffect(() => {
    if (survey) {
      calculateOptionColWidth();
    }
  }, [survey])

  const calculateOptionColWidth = () => {
    if (survey) {
      const _narrow = window.innerWidth < 925;
      let mainWidth: number;
      let w: number;
      let _answerCols: number;
      if (_narrow) {
        mainWidth = window.innerWidth - 32;
        _answerCols = survey.answerOptions;
        w = mainWidth/ _answerCols;
      } else {
        mainWidth = window.innerWidth < 1656 ? window.innerWidth - 112 : 1544;
        _answerCols = survey.answerOptions + 2;
        w = (mainWidth - nameColWidth)/ _answerCols;
      }
      // console.log("window width " + window.innerWidth + " main width " + mainWidth + " answerCols " + _answerCols + " col width " + w)
      setOptionColWidth(w);
      setNarrow(_narrow);
      setAnswerCols(_answerCols);
    }
  }

  const sendResponses = () => {
    const anonymous = !!token;
    api.post(url, survey, [], anonymous).then((response) => {
      if (response.json.status === "ok") {
        setShowFeedbackSent(true);
      }
    })
  }

  const formatError = () => {
    let errorHeader = "Unknown error: " + error;
    let errorBody =
      lang === "sv"
        ? "Kontakta support@zebrain.se så hjälper vi dig"
        : "Contact support@zebrain.se and we will help you";
    if (error === "deadline-passed" || error === "already-submitted") {
      errorHeader = t(error);
      if (token) {
        errorBody = t(error + "-message");
      } else {
        errorBody = lang === "sv" ? "Du har redan skickat din feedback" :
          "You have already submitted your feedback"
      }

    } else if (error === "badToken") {
      errorHeader =
        lang === "sv"
          ? "Något är konstigt med din token"
          : "Something is wrong with your token";
    } else if (error === "badRecipient") {
      errorHeader =
        lang === "sv"
          ? "Du är inte registrerad mottagare"
          : "You are not a registered recipient";
    }
    return (
      <div className="mt-10 main-content">
        <div className="flex flex-col h-full flex-grow min-h-0 gap-6 mt-6">
          {error === "deadline-passed" && (
            <CalendarXIcon
              alt="Calendar X icon"
              className="w-12 h-12 mx-auto text-neutral-400"
            />
          )}
          <div className="flex flex-col gap-2">
            <h1 className="z-heading1 text-white text-center">
              {errorHeader}
            </h1>
            <div
              className="text-gray-400 text-center"
              dangerouslySetInnerHTML={{ __html: errorBody }}
            />
          </div>
          {
            (error === "already-submitted" && !token) &&
              <CustomButton buttonType="blueBase" className="mt-4 mx-auto" style={{width: 250}}
                            onClick={() => navigate("/assessments")}>
                {lang === "sv" ? "Tillbaka till mätningar" : "Back to assessments"}
              </CustomButton>
          }
          {(error === "deadline-passed" || error === "already-submitted") &&
            renderOtherSurveys()}
        </div>
      </div>
    );
  };

  const renderOtherSurveys = () => {
    if (otherSurveys.length === 0) return null
    return (
      <div className="mt-6">
        <p className="font-medium">
          {lang === "sv"
            ? "Andra feedback-förfrågningar du inte har svarat på än"
            : "Other feedback requests you have not answered yet"}
          :
        </p>
        {otherSurveys.map((otherSurvey, index) => (
          <div
            className="mt-2 flex justify-between cursor-pointer bg-indigo-900 rounded-md p-4 hover:bg-indigo-800"
            key={"other_q_" + index}
            onClick={() =>
              (window.location.href =
                "/feedback/survey/" +
                otherSurvey.id +
                "?token=" +
                otherSurvey.token)
            }
          >
            <p className="w-40 truncate">{otherSurvey.name}</p>
            <p className="w-40 text-gray-400">
              Deadline {formatDate(otherSurvey.deadline, false, lang)}
            </p>
            <ChevronRightIcon className="h-6 w-6 text-white!" aria-hidden="true" />
          </div>
        ))}
      </div>
    );
  }

  const renderProgressBar = () => {
    if (survey.sections.length < 2) return null;
    let percent = (100 * (sectionNr + 1) / survey.sections.length) + "%";
    return (
      <div className="flex items-center gap-x-1 my-6">
        <div className="h-2 bg-indigo-700 rounded-full" style={{width: "calc(100% - 80px)"}}>
          <div className="h-2 w-full bg-primary-600 rounded-full" style={{width: percent}}></div>
        </div>
        <div className="z-body2">
          {t("step")} {sectionNr + 1} {t("of")} {survey.sections.length}
        </div>
      </div>
    )
  }

  const renderSelfInfo = () => {
    return (
      <Modal isOpen={selfInfo} onClose={() => setSelfInfo(false)}>
        <ModalHeader onClose={() => setSelfInfo(false)}>
          <div className="z-title1">{t("self-assessment")}</div>
        </ModalHeader>
        <ModalBody>
          <div className="text-gray-400">{t("self-assessment-txt1")}</div>

          <div className="text-gray-400 mt-4">
            {t("self-assessment-txt2")}
          </div>
        </ModalBody>
      </Modal>
    );
  };

  if (error) {
    return formatError();
  }

  if (showFeedbackSent) {
    return (
      <div className="mt-10 main-content">
        <div className="flex flex-col h-full grow min-h-0 gap-6 mt-6">
          <BadgeCheckIcon
            aria-hidden="true"
            className="w-12 h-12 mx-auto text-success-500"
          />
          <div className="flex flex-col gap-2">
            <h1 className="z-heading1 text-white text-center">
             {t("feedback-sent")}
            </h1>

            <div>
              <p className="text-gray-400 text-center">
                {t("thank-you-for-your-feedback")}
              </p>
              {renderOtherSurveys()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading || data === null) {
    return null;
  }

  const updateQuestion = (questionId: string, value: string, what: string, feedbackNr: number) => {
    const _data = {...data}
    // console.log('Setting answer ' + value + ' for questionId ' + questionId + ' person ' + feedbackNr);
    for (const _section of survey.sections) {
      for (const _question of _section.questions) {
        if (_question.questionId === questionId) {
          if (what === "a") {
            // console.log(_question.questionId + ' === ' + questionId + ' = ' + value);
            _question.answers[feedbackNr].answer = parseInt(value);
            let questionComplete = true;
            for (const a of _question.answers) {
              if (a.answer < 0) {
                questionComplete = false;
                break;
              }
            }
            _question.answer = questionComplete ? 1 : -1;
          } else if (what === "text") {
            _question.answers[feedbackNr].freeAnswer = value;
          } else if (what === "showText") {
            let showFree = _question.answers[feedbackNr].showFreeAnswer;
            if (showFree) {
              // Only hide free answer if it is empty
              showFree = _question.answers[feedbackNr].freeAnswer.length > 0;
            } else {
              showFree = true;
            }
            _question.answers[feedbackNr].showFreeAnswer = showFree;
          }
          break;
        }
      }
    }
    setData(_data);
  }

  const surveyNotComplete = () => {
    for (const section of survey.sections) {
      for (const question of section.questions) {
        if (question.answer < 0) {
          return true;
        }
      }
    }
    return false;
  }

  const sectionNotComplete = () => {

    for (const question of survey.sections[sectionNr].questions) {
      if (question.answer < 0) {
        return true;
      }
    }
    return false;
  }

  const formatQuestion = (question: QuestionInterface) => {

    let questionText = question.names[language];
    if (questionText.startsWith("X ")) {
      questionText = "... " + questionText.substring(2);
    }
    const lastAnswerOption = answerOptions[answerOptions.length - 1];
    return (
      <>
        <div className="mt-4 z-badge transparent text-gray-600">
          {lang === "sv" ? "Fråga " : "Question "}{question.questionNr}
        </div>
        <p className="z-heading2 mt-4 text-neutral-300!">{questionText}</p>
        <div className="mt-6 flex items-end">
          {
            !narrow && <div style={{width: nameColWidth}}></div>
          }
          {
            answerOptions.filter((a, i) => i < answerCols)
              .map((a, i) => (
              <div className="text-center z-body2" style={{width: optionColWidth}} key={"option_" + i}>{a.name}</div>
            ))
          }
          {
            !narrow &&
              <div style={{width: optionColWidth}} className="text-center z-body2">
                {lang === 'sv' ? 'Kommentar' : 'Comment'}
              </div>
          }
        </div>

        <hr className="mt-1 border-indigo-800" style={{marginLeft: nameColWidth}}/>
        {
          feedbackRecipients.map((fr, feedbackNr) => (
            <div key={"fr_" + fr.clientId + "_" + question.questionId} className="mt-4">
              {
                narrow &&
                  <div className="z-title2 mb-2">{fr.name === "self" ? t("you") : fr.name}</div>
              }
              <div className="flex items-center">
                {
                  !narrow &&
                    <div style={{width: nameColWidth}} className="z-title2">{fr.name === "self" ? t("you") : fr.name}</div>
                }
                <div className="flex items-center">
                  {
                    answerOptions.filter((a, i) => i < answerCols).map((a, i) => {
                      if (a.value === "0" && fr.name === "self") {
                        return null;
                      }
                      return (
                        <div style={{width: optionColWidth}} className="px-auto text-center"
                             key={"a_" + question.questionId + "_" + i + "_" + feedbackNr}>
                          <RadioButton id={question.questionId + i + "_" + feedbackNr} value={i + ""}
                                       name={"A " + i} label=""
                                       justify="justify-center"
                                       onChange={() => updateQuestion(question.questionId, a.value, "a", feedbackNr)}
                                       checked={question.answers[feedbackNr].answer === parseInt(a.value)}/>
                        </div>
                      )
                    })
                  }
                  {
                    (!narrow && fr.name != "self") &&
                      <div style={{width: optionColWidth}} className="cursor-pointer text-center"
                           onClick={() => updateQuestion(question.questionId, "", "showText", feedbackNr)}>
                          <div
                              className={"mx-auto border border-indigo-700 rounded-full inline-block px-4 py-1" + (question.answers[feedbackNr].showFreeAnswer ? " bg-indigo-700" : "")}>
                              <ChatBubbleIcon aria-hidden="true" className="w-6 h-6 mx-auto text-neutral-400" />
                          </div>
                      </div>
                  }

                </div>
              </div>
              {
                (narrow && fr.name != "self") &&
                  <div className="mt-4 flex justify-between">
                      <div className="px-auto text-center" style={{marginLeft: (optionColWidth/2 - 16)}}>
                          <RadioButton id={question.questionId + answerCols + "_" + feedbackNr}
                                       value={lastAnswerOption.value + ""}
                                       name={"A " + lastAnswerOption.value} label={lastAnswerOption.name}
                                       justify="justify-center"
                                       onChange={() => updateQuestion(question.questionId, lastAnswerOption.value, "a", feedbackNr)}
                                       checked={question.answers[feedbackNr].answer === parseInt(lastAnswerOption.value)}/>
                      </div>
                      <div className="cursor-pointer"
                           onClick={() => updateQuestion(question.questionId, "", "showText", feedbackNr)}>
                          <div
                              className={"flex items-center mx-auto border border-indigo-700 rounded-full px-4 py-1" + (question.answers[feedbackNr].showFreeAnswer ? " bg-indigo-700" : "")}>
                            <ChatBubbleIcon aria-hidden="true" className="w-6 h-6 mx-auto text-neutral-400" />
                            <div className="ml-2">{lang === 'sv' ? 'Kommentar' : 'Comment'}</div>
                          </div>
                      </div>
                  </div>
              }
              {
                (question.freeText && question.answers[feedbackNr].showFreeAnswer) &&
                  <div className="mt-4" style={{marginLeft: narrow ? 0 : nameColWidth}}>
                    <textarea className="z-input" value={question.answers[feedbackNr].freeAnswer}
                              placeholder="Add context to your rating"
                              onChange={(e) => updateQuestion(question.questionId, e.target.value, "text", feedbackNr)}></textarea>
                  </div>
              }
              <hr className="my-4 border-indigo-800"/>
            </div>
          ))
        }
      </>
    )
  }

  const changeLanguage = (l: string) => {
    if (survey.languages.indexOf(l) !== -1) {
      setLanguage(l);
      if (survey) {
        formatAnswerOptions(survey, l);
      }
    }
    if (l === 'en' || l === 'sv') {
      i18n.changeLanguage(l).then();
    }
  }

  const showChangeLanguage = () => {
    if (survey.languages.length < 2 || sectionNr > 0) return null;
    return (
      <div>
        <select className="z-select" value={language}
                onChange={(e) => changeLanguage(e.target.value)}>
          {
            survey.languages.map((l, i) => {
              return (
                <option key={"lang_" + i} value={l}>
                  {languageMap[l]}
                </option>
              )
            })
          }
        </select>
      </div>
    )
  }

  let thisSection = survey.sections[sectionNr]

  return (
    <div className="main-max">
      {renderProgressBar()}
      {selfInfo && renderSelfInfo()}

      <div className="flex justify-between items-center">
        <h1 className="z-heading1 text-white mt-4">
          <div className="flex items-center">
            <div>{t("feedback")}</div>
            <div className="ml-2 p-1 flex item-center justify-center cursor-pointer  rounded-[20px]
            hover:bg-indigo-700" onClick={() => setSelfInfo(true)}><InformationCircleIcon alt="info"/></div>
          </div>
        </h1>
        {showChangeLanguage()}
      </div>

      <div>

        <div className="mt-4">
          <p className="text-2xl mb-4">{thisSection.names[language]}</p>
          {
            thisSection.questions.map(question => {
              return (
                <div className="mt-4" key={"question_" + question.questionId}>
                  {formatQuestion(question)}
                </div>
              )
            })
          }
        </div>

      </div>

      {showAlert && (
        <div className="flex items-center rounded-md border border-cherry-500 bg-cherry-200 text-indigo-1000 p-4 gap-2">
          <AlertIcon aria-label="Alert" className="w-6 h-6 text-cherry-500!"/>
          <span>{t("feedback-response-form-alert")}</span>
        </div>
      )}
      <div className="flex justify-center gap-x-4 mt-4">
        {
          sectionNr > 0 &&
            <CustomButton onClick={() => setSectionNr(sectionNr - 1)} buttonType="outlineWhiteBase"
                          style={{width: "unset"}}>
              {t("back")}
            </CustomButton>
        }
        {
          (sectionNr === survey.sections.length - 1) ? (
            <CustomButton disabled={surveyNotComplete()} onClick={() => sendResponses()}
                          style={{width: "unset"}}>
              {t("submit")}
            </CustomButton>
          ) : (
            <CustomButton disabled={sectionNotComplete()} onClick={() => setSectionNr(sectionNr + 1)}
                          buttonType="blueBase" style={{width: "unset"}}>
              {t("next")}
            </CustomButton>
          )
        }
      </div>

      <div className="h-20">&nbsp;</div>
    </div>
  );
};

export default SurveyMultiResponse;
